// Переходы приложения: боковое меню с подписями на компьютере, нижняя панель
// на телефоне — раскладка teen-english, решение владельца от 28 октября 2026.
//
// **Почему подписи вернулись.** С 24 сентября 2026 переходы стояли рядом
// значков в шапке, а подписи жили в `title`. Значков стало семь, и на скриншоте
// «Сегодня» ни один не говорил, куда ведёт: пользователь, пришедший впервые,
// не отличал «Историю» от «Прогресса». Причина, по которой подписи убирали —
// четыре слова съедали правую половину шапки и читались раньше заголовка, —
// уходит вместе с шапкой: в боковой колонке подписи никому не мешают.
//
// **На задаче переходы есть**, как и с 24 сентября: это выход, а не приглашение,
// и стоит он там же, где на остальных экранах — сбоку, вне потока чтения.
//
// Черновик здесь — отдельный, сам по себе, не тот, что привязан к задаче:
// см. src/screens/Scratchpad.tsx.

import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  IconCalendar,
  IconDraft,
  IconGraphLab,
  IconHistory,
  IconMap,
  IconDots,
  IconPerson,
  IconSolve,
  IconTrend,
  IconX,
} from "./Icons";
import { ThemeSwitch } from "./ThemeSwitch";
import { useDialog } from "./useDialog";

export type NavDestination =
  | "today"
  | "plan"
  | "graph"
  | "scratch"
  | "history"
  | "profile"
  | "atlas"
  | "progress";

type Item = {
  key: NavDestination;
  label: string;
  /** Подпись в нижней панели телефона, где на кнопку пять букв ширины. */
  short: string;
  icon: (props: { size?: number }) => React.ReactElement;
};

const TODAY: Item = { key: "today", label: "Сегодня", short: "Сегодня", icon: IconSolve };
const PLAN: Item = { key: "plan", label: "План", short: "План", icon: IconCalendar };
const ATLAS: Item = { key: "atlas", label: "Карта навыков", short: "Карта", icon: IconMap };
const PROGRESS: Item = { key: "progress", label: "Прогресс", short: "Прогресс", icon: IconTrend };
const HISTORY: Item = { key: "history", label: "История", short: "История", icon: IconHistory };
const GRAPH: Item = { key: "graph", label: "Лаборатория графиков", short: "Графики", icon: IconGraphLab };
const SCRATCH: Item = { key: "scratch", label: "Черновики", short: "Черновики", icon: IconDraft };
const PROFILE: Item = { key: "profile", label: "Личные данные", short: "Профиль", icon: IconPerson };

// Порядок: сперва работа, потом то, куда уходят пробовать. «Сегодня» первым —
// единственный переход, ради которого сюда приходят не отвлечься, а вернуться.
const STUDY = [TODAY, PLAN, ATLAS, PROGRESS, HISTORY];
const TOOLS = [GRAPH, SCRATCH];
// В нижней панели помещается пять кнопок: четыре занятия и «Ещё». История —
// в «Ещё»: в неё заглядывают реже, чем в план (макет, 28 октября 2026).
const TABS = [TODAY, PLAN, ATLAS, PROGRESS];
const MORE = [HISTORY, GRAPH, SCRATCH, PROFILE];

type NavProps = {
  /** Экран, на котором ученик сейчас, если он есть среди переходов. */
  current?: NavDestination | null;
  onGo: (to: NavDestination) => void;
};

function Link({ item, current, onGo }: { item: Item } & NavProps) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className="side-link"
      aria-current={current === item.key ? "page" : undefined}
      onClick={() => onGo(item.key)}
    >
      <Icon size={20} />
      <span>{item.label}</span>
    </button>
  );
}

/** Содержимое боковой колонки под знаком продукта. */
export function AppNav({ current, onGo }: NavProps) {
  return (
    <>
      <nav className="side-nav" aria-label="Переходы">
        <p className="side-label">Занятия</p>
        {STUDY.map((item) => (
          <Link key={item.key} item={item} current={current} onGo={onGo} />
        ))}
        <p className="side-label">Инструменты</p>
        {TOOLS.map((item) => (
          <Link key={item.key} item={item} current={current} onGo={onGo} />
        ))}
      </nav>
      <div className="side-foot">
        <Link item={PROFILE} current={current} onGo={onGo} />
        <ThemeSwitch className="side-link" label="Оформление" />
      </div>
    </>
  );
}

/** Нижняя панель телефона: четыре занятия и «Ещё» с остальным. */
export function TabBar({ current, onGo }: NavProps) {
  const [more, setMore] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const inMore = MORE.some((m) => m.key === current);
  const go = (to: NavDestination) => {
    setMore(false);
    onGo(to);
  };
  return (
    <>
      <nav className="tabbar" aria-label="Переходы">
        {TABS.map(({ key, short, icon: Icon }) => (
          <button
            key={key}
            type="button"
            aria-current={current === key ? "page" : undefined}
            onClick={() => go(key)}
          >
            <Icon size={20} />
            <span>{short}</span>
          </button>
        ))}
        <button
          ref={opener}
          type="button"
          aria-current={inMore ? "page" : undefined}
          aria-haspopup="dialog"
          aria-expanded={more}
          onClick={() => setMore(true)}
        >
          <IconDots size={20} />
          <span>Ещё</span>
        </button>
      </nav>
      {more ? (
        <MoreSheet
          current={current}
          onGo={go}
          onClose={() => {
            setMore(false);
            opener.current?.focus();
          }}
        />
      ) : null}
    </>
  );
}

function MoreSheet({ current, onGo, onClose }: NavProps & { onClose: () => void }) {
  const first = useRef<HTMLButtonElement>(null);
  useDialog(first, onClose);
  // В корень страницы, а не на месте кнопки: у боковой колонки и нижней
  // панели мягкого стиля включён backdrop-filter, а он делает элемент рамкой
  // для position: fixed внутри. Окно вставало по краю колонки в 250 px и
  // обрезалось (замечание владельца, 31 октября 2026).
  return createPortal(
    <>
      <div className="look-scrim" onClick={onClose} />
      <aside className="more-sheet" role="dialog" aria-modal="true" aria-label="Ещё">
        <header>
          <b>Ещё</b>
          <button ref={first} type="button" className="look-close" onClick={onClose} aria-label="Закрыть">
            <IconX />
          </button>
        </header>
        {MORE.map((item) => (
          <Link key={item.key} item={item} current={current} onGo={onGo} />
        ))}
        <ThemeSwitch className="side-link" label="Оформление" />
      </aside>
    </>,
    document.body,
  );
}
